import React from "react";
import { Link } from "react-router-dom";
import { Phone, CheckCircle2, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import ServiceIcon from "@/components/ServiceIcon";
import CTASection from "@/components/CTASection";
import { SERVICES, COMPANY } from "@/data/site";

const HEADER_IMG = "https://images.unsplash.com/photo-1676210133055-eab6ef033ce3";

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* HEADER */}
      <section className="relative bg-[#0F172A] py-20 lg:py-28">
        <img
          src={HEADER_IMG}
          alt="Plumber working on pipes"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          loading="eager"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#0284C7]">
            Our Services
          </span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.05]">
            Plumbing &amp; heating, done right
          </h1>
          <p className="mt-5 max-w-2xl text-base lg:text-lg text-slate-200">
            Residential and commercial services in Stoughton, MA. Licensed,
            insured, and ready to help.
          </p>
        </div>
      </section>

      {/* SERVICE LIST */}
      <section className="bg-white py-16 lg:py-24" data-testid="services-list-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-px bg-slate-200 border border-slate-200">
          {SERVICES.map((s, i) => (
            <div
              key={s.slug}
              data-testid={`service-detail-${s.slug}`}
              className="bg-white p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start"
            >
              <div className="lg:col-span-1 flex lg:block items-center gap-4">
                <span className="inline-flex h-14 w-14 items-center justify-center bg-[#0284C7] text-white">
                  <ServiceIcon name={s.icon} className="h-7 w-7" />
                </span>
                <div className="lg:hidden font-display text-xs tracking-[0.18em] uppercase text-slate-400">
                  {String(i + 1).padStart(2, "0")} / {SERVICES.length}
                </div>
              </div>
              <div className="lg:col-span-7">
                <div className="hidden lg:block font-display text-xs tracking-[0.18em] uppercase text-slate-400 mb-2">
                  {String(i + 1).padStart(2, "0")} / {SERVICES.length}
                </div>
                <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-[#0F172A]">
                  {s.title}
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  {s.description}
                </p>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                  {s.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#0284C7] flex-shrink-0" strokeWidth={1.75} />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <a
                  href={`tel:${COMPANY.phoneTel}`}
                  data-testid={`service-${s.slug}-call-link`}
                  className="inline-flex w-full lg:w-auto"
                >
                  <Button className="w-full lg:w-auto rounded-none bg-[#0284C7] hover:bg-[#0369A1] text-white font-semibold px-6 py-5">
                    <Phone className="h-4 w-4 mr-2" strokeWidth={1.75} />
                    Call for Service
                  </Button>
                </a>
                <Link
                  to="/quote"
                  data-testid={`service-${s.slug}-quote-link`}
                  className="inline-flex w-full lg:w-auto"
                >
                  <Button
                    variant="outline"
                    className="w-full lg:w-auto rounded-none border-2 border-[#0F172A] text-[#0F172A] hover:bg-[#0F172A] hover:text-white font-semibold px-6 py-5"
                  >
                    Get Free Quote
                    <ArrowRight className="h-4 w-4 ml-2" strokeWidth={1.75} />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTASection
        title="Have a plumbing issue? Let's solve it today."
        subtitle="Call us now for fast service or request a free quote — we're your local plumbing team in Stoughton, MA."
      />
    </div>
  );
}
